import { Card, CardContent } from '@design-system/Card';
import { Link } from '@design-system/Link';
import { Skeleton } from '@design-system/Skeleton';
import { ChevronRightIcon } from '@/components/icons/ChevronRightIcon';
import { SMP_ORDER_DETAILS_PATH } from '@/constants/paths';
import { useOrderTrackingViewModel } from '@/features/OrderTrackingViewModels';
import { cn } from '@/utils/styles';

export const SMPLandingPageOrderInProgress = () => {
  const {
    isLoading,
    isError,
    hasOrder,
    progressPercentage,
    stageLabels,
    activeStep,
  } = useOrderTrackingViewModel();

  if (isLoading) {
    return (
      <Card
        className="border-0 bg-background-blur-bold"
        data-testid="smp-orderInProgress-skeleton"
      >
        <CardContent className="flex flex-col gap-4 p-6">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-1 w-full rounded-full" />
          <div className="flex justify-between">
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-14" />
            <Skeleton className="h-4 w-16" />
          </div>
          <Skeleton className="h-10 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (isError || !hasOrder) return null;

  return (
    <Card
      className="border-0 bg-background-blur-bold"
      data-testid="smp-orderInProgress-card"
    >
      <CardContent
        className="flex flex-col gap-4 p-6"
        data-testid="smp-orderInProgress-content"
      >
        <span
          className="typography-h3 text-[#146464] mb-4"
          data-testid="smp-orderInProgress-statusText"
        >
          {activeStep?.header_text ?? 'Order Status'}
        </span>

        <div
          data-testid="smp-orderInProgress-progressBarContainer"
          className="w-full h-1 bg-background-secondary rounded-full relative"
        >
          <div
            data-testid="smp-orderInProgress-progressFill"
            className="h-full bg-background-success transition-all duration-300 ease-in-out"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>

        <div
          className="flex justify-between"
          data-testid="smp-orderInProgress-stageLabels"
        >
          {stageLabels.map((label, index) => (
            <span
              key={label}
              className={cn(
                'typography-caption2',
                activeStep?.smp_status === label
                  ? 'text-primary font-medium'
                  : 'text-secondary',
              )}
              data-testid={`smp-orderInProgress-state-${index}`}
            >
              {label}
            </span>
          ))}
        </div>

        <Link
          to={SMP_ORDER_DETAILS_PATH}
          variant="flat"
          className="flex items-center gap-1 typography-body text-primary pt-2"
          data-testid="smp-orderInProgress-detailsLink"
        >
          <span>Order details</span>
          <ChevronRightIcon className="size-6" aria-hidden />
        </Link>
      </CardContent>
    </Card>
  );
};
